import { motion, useInView } from "framer-motion";
import { Element } from 'react-scroll';
import { FaComments, FaArrowRight, FaCheck, FaChartLine, FaClock } from 'react-icons/fa';
import { useState, useEffect, useRef } from 'react';

interface ItemCard {
    name: string;
    description: string;
    price: number;
    image: string;
    id: string;
}

interface DemoMessage {
    type: 'ai' | 'customer';
    text: string;
    delay: number;
    itemCard?: ItemCard;
    suggestions?: string[];
}

const AIChatbotSection = () => {
    const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
    const [hasAnimationStarted, setHasAnimationStarted] = useState(false);
    const sectionRef = useRef<HTMLDivElement | null>(null);
    const isSectionInView = useInView(sectionRef, { once: true, amount: 0.3 });

    const demoMessages: DemoMessage[] =  [
        {
            type: 'ai',
            text: "Welcome to our restaurant! 👋 How can I help you today?",
            delay: 0.5
        },
        {
            type: 'customer',
            text: "Hi! What appetizers do you recommend for sharing?",
            delay: 2
        },
        {
            type: 'ai',
            text: "Great choice! For sharing, I recommend our popular appetizers:",
            delay: 3.5,
            itemCard: {
                name: "Wings Platter",
                description: "Crispy wings with 3 sauces: Buffalo, Honey BBQ, and Garlic Parmesan",
                price: 16.99,
                image: "🍗",
                id: "wings"
            }
        },
        {
            type: 'customer',
            text: "The wings sound perfect!",
            delay: 5.5
        },
        {
            type: 'ai',
            text: "Excellent choice! What would you like for your main course?",
            delay: 7,
            itemCard: {
                name: "Grilled Chicken Burger",
                description: "Juicy grilled chicken with lettuce, tomato, swiss cheese & special sauce. Comes with crispy fries",
                price: 14.99,
                image: "🍔",
                id: "burger"
            }
        },
        {
            type: 'customer',
            text: "The burger looks great!",
            delay: 9
        },
        {
            type: 'ai',
            text: "Perfect pairing! How about a refreshing drink?",
            delay: 10.5,
            itemCard: {
                name: "Ice Cold Lemonade",
                description: "Freshly squeezed lemonade with a hint of mint",
                price: 5.99,
                image: "🍋",
                id: "lemonade"
            }
        },
        {
            type: 'customer',
            text: "Yes, that sounds perfect!",
            delay: 12.5
        },
        {
            type: 'ai',
            text: "Wonderful! Your order is ready. Total: $37.97 🎉",
            delay: 14,
            suggestions: ["Proceed to Checkout", "Add more items", "View full menu"]
        }
    ];

    const [orderItems, setOrderItems] = useState<string[]>([]);

    const messageCount = demoMessages.length;

    const handleAddToOrder = (itemId: string) => {
        if (!orderItems.includes(itemId)) {
            setOrderItems(prev => [...prev, itemId]);
        }
    };

    useEffect(() => {
        if (isSectionInView) {
            setHasAnimationStarted(true);
            setCurrentMessageIndex(0);
        }
    }, [isSectionInView]);

    // Auto-advance messages
    useEffect(() => {
        if (!hasAnimationStarted) {
            return;
        }

        let resetTimeout: ReturnType<typeof setTimeout> | null = null;

        const timer = setInterval(() => {
            setCurrentMessageIndex(prev => {
                if (prev < messageCount - 1) {
                    return prev + 1;
                }

                if (resetTimeout) {
                    clearTimeout(resetTimeout);
                }

                resetTimeout = setTimeout(() => setCurrentMessageIndex(0), 2000);
                return prev;
            });
        }, 3000);

        return () => {
            clearInterval(timer);
            if (resetTimeout) {
                clearTimeout(resetTimeout);
            }
        };
    }, [hasAnimationStarted, messageCount]);

    // Auto scroll to bottom
    useEffect(() => {
        const chatContainer = document.getElementById('chat-container');
        if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
        }
    }, [currentMessageIndex]);

    const visibleMessages = demoMessages.slice(0, currentMessageIndex + 1);

    const getOrderTotal = () => {
        let total = 0;
        if (orderItems.includes('wings')) total += 16.99;
        if (orderItems.includes('burger')) total += 14.99;
        if (orderItems.includes('lemonade')) total += 5.99;
        return total.toFixed(2);
    };

    // const features = [
    //     {
    //         icon: FaBrain,
    //         title: "Smart Menu Navigation",
    //         description: "AI guides customers from appetizers to mains to drinks, suggesting perfect combinations",
    //         stats: "95% completion rate"
    //     },
    //     {
    //         icon: FaComments,
    //         title: "Natural Food Conversations",
    //         description: "Customers ask about appetizers, spice levels, portions - AI understands and responds naturally",
    //         stats: "24/7 availability"
    //     },
    //     {
    //         icon: FaLightbulb,
    //         title: "Progressive Upselling",
    //         description: "Seamlessly suggests drinks, sides, and upgrades throughout the ordering process",
    //         stats: "45% higher order value"
    //     },
    //     {
    //         icon: FaUsers,
    //         title: "Contextual Recommendations",
    //         description: "Remembers customer preferences and suggests appetizers based on main course choices",
    //         stats: "92% customer satisfaction"
    //     }
    // ];

    const benefits = [
        { text: "Reduce order confusion by 90%", icon: FaCheck },
        { text: "Increase average order value by 40%", icon: FaChartLine },
        { text: "Save 3+ hours daily on order management", icon: FaClock },
        { text: "Support 50+ languages automatically", icon: FaComments },
    ];

    return (
        <Element id="ai-chatbot" name="ai-chatbot">
            <section ref={sectionRef} className="py-10 pb-10 bg-white/40 relative overflow-hidden border-b-2 border-orange-100">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute top-20 left-20 w-32 h-32 bg-orange-500 rounded-full"></div>
                    <div className="absolute top-40 right-32 w-24 h-24 bg-indigo-500 rounded-full"></div>
                    <div className="absolute bottom-32 left-1/3 w-40 h-40 bg-purple-500 rounded-full"></div>
                </div>

                <div className="max-w-7xl mx-auto px-6 relative">
                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-10"
                    >


                        <h2 className="text-4xl lg:text-6xl font-bold mb-6">
                            Meet Your AI
                            <br />
                            <span className="bg-gradient-to-r from-orange-600 via-orange-300 to-orange-600 bg-clip-text text-transparent">
                                Restaurant Assistant
                            </span>
                        </h2>

                        <p className="text-xl lg:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
                            Our intelligent chatbot doesn't just take orders, it understands your customers,
                            suggests the perfect dishes, and turns every conversation into a sale.
                        </p>
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        {/* Interactive Demo */}
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="relative mb-10 -mx-6 [@media(min-width:375px)]:mx-0"
                        >
                            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
                                {/* Chat Header */}
                                <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4 text-white">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                                            <img src="/Ai_Model.webp" alt="Robot" className="w-10 pb-2" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold">Dine.AI Assistant</h4>
                                            <p className="text-sm opacity-90">Online • Ready to help</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Chat Messages */}
                                <div className="p-6 h-96 overflow-y-auto bg-gradient-to-b from-gray-50 to-white" id="chat-container">
                                    <div className="space-y-4">
                                        {visibleMessages.map((message, index) => (
                                            <motion.div
                                                key={`${currentMessageIndex}-${index}`}
                                                initial={{ opacity: 0, y: 12 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.35 }}
                                                className={`flex ${message.type === 'customer' ? 'justify-end' : 'justify-start'}`}
                                            >
                                                <div className={`max-w-xs px-4 py-3 rounded-2xl ${message.type === 'customer'
                                                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white ml-4 shadow-lg'
                                                    : 'bg-white border border-gray-200 text-gray-800 mr-4 shadow-sm'
                                                    }`}>
                                                    {message.type === 'ai' && (
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <img src="/Ai_Model.webp" alt="Robot" className="w-8 " />
                                                            <span className="text-xs font-medium text-orange-600">AI Assistant</span>
                                                        </div>
                                                    )}
                                                    <p className="text-sm leading-relaxed">{message.text}</p>

                                                    {/* Item Card */}
                                                    {message.itemCard && (
                                                        <div className="mt-3 bg-gradient-to-br from-orange-50 to-white border-2 border-orange-200 rounded-xl p-4 shadow-lg">
                                                            <div className="flex gap-3 mb-3">
                                                                <div className="text-5xl">{message.itemCard.image}</div>
                                                                <div className="flex-1">
                                                                    <h4 className="font-bold text-gray-800 mb-1">{message.itemCard.name}</h4>
                                                                    <p className="text-xs text-gray-600 mb-2">{message.itemCard.description}</p>
                                                                    <div className="flex  flex-col justify-between">
                                                                        <span className="text-lg font-bold text-orange-600">
                                                                            ${message.itemCard.price}
                                                                        </span>
                                                                        {orderItems.includes(message.itemCard.id) ? (
                                                                            <div className="flex items-center gap-1 text-green-600 text-sm font-medium">
                                                                                <FaCheck className="text-xs" />
                                                                                Added
                                                                            </div>
                                                                        ) : (
                                                                            <motion.button
                                                                                whileHover={{ scale: 1.05 }}
                                                                                whileTap={{ scale: 0.95 }}
                                                                                onClick={() => message.itemCard && handleAddToOrder(message.itemCard.id)}
                                                                                className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:shadow-lg transition-all"
                                                                            >
                                                                                Add to Order
                                                                            </motion.button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {message.suggestions && index === visibleMessages.length - 1 && (
                                                        <div className="mt-3 space-y-2">
                                                            {message.suggestions.map((suggestion, i) => (
                                                                <button
                                                                    key={i}
                                                                    className="block w-full text-left px-3 py-2 bg-orange-50 hover:bg-orange-100 rounded-lg text-xs text-orange-700 transition-all duration-200 border border-orange-200 hover:border-orange-300"
                                                                >
                                                                    {suggestion}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </motion.div>
                                        ))}

                                        {/* Typing Indicator */}
                                        {currentMessageIndex < demoMessages.length - 1 && demoMessages[currentMessageIndex + 1]?.type === 'ai' && (
                                            <div className="flex justify-start">
                                                <div className="bg-white border border-gray-200 text-gray-800 mr-4 shadow-sm px-4 py-3 rounded-2xl">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <img src="/Ai_Model.webp" alt="Robot" className="w-8 " />
                                                        <span className="text-xs font-medium text-orange-600">AI Assistant</span>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" />
                                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" />
                                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Chat Input */}
                                <div className="px-6 py-4 bg-gray-50 border-t">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="text"
                                            placeholder="Ask about appetizers, main courses, drinks..."
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                            readOnly
                                        />
                                        <button className="w-10 h-10 bg-orange-500 text-white rounded-full flex items-center justify-center hover:bg-orange-600 transition-colors">
                                            <FaArrowRight />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {/* Order Summary Floating Card */}
                            {orderItems.length > 0 && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8, x: 20 }}
                                    animate={{ opacity: 1, scale: 1, x: 0 }}
                                    className="absolute top-1/2 -right-16 bg-white rounded-xl shadow-xl p-4 border border-orange-200 z-10 w-48 hidden lg:block"
                                >
                                    <h4 className="font-bold text-gray-800 text-sm mb-2">🛒 Current Order</h4>
                                    <div className="space-y-1 text-xs text-gray-600">
                                        {orderItems.includes('wings') && (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="flex justify-between"
                                            >
                                                <span>Wings Platter</span>
                                                <span>$16.99</span>
                                            </motion.div>
                                        )}
                                        {orderItems.includes('burger') && (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="flex justify-between"
                                            >
                                                <span>Chicken Burger</span>
                                                <span>$14.99</span>
                                            </motion.div>
                                        )}
                                        {orderItems.includes('lemonade') && (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="flex justify-between"
                                            >
                                                <span>Lemonade</span>
                                                <span>$5.99</span>
                                            </motion.div>
                                        )}
                                        <hr className="my-2" />
                                        <div className="flex justify-between font-bold text-orange-600">
                                            <span>Total:</span>
                                            <span>${getOrderTotal()}</span>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </motion.div>

                        {/* Robot Image */}
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="items-center pb-30 justify-center hidden lg:flex"
                        >
                            <motion.div
                                animate={{
                                    y: [0, -20, 0]
                                }}
                                transition={{
                                    duration: 4,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                }}
                                className="relative"
                            >
                                <img
                                    src="/Ai_Model.webp"
                                    alt="AI Restaurant Assistant Robot"
                                    className="w-[900px] h-[700px] object-contain drop-shadow-2xl"
                                />
                            </motion.div>
                        </motion.div>
                    </div>

                    {/* Benefits Grid */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100"
                    >
                        <h3 className="text-2xl font-bold text-center mb-8 text-gray-800">
                            Transform Your Restaurant Operations
                        </h3>

<div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
    {benefits.map((benefit, index) => (
        <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className="group lg:text-center flex lg:flex-col flex-row items-center text-left"
        >
            <div className="min-h-[64px] min-w-[64px] lg:mx-auto lg:mb-4 bg-gradient-to-br from-orange-400 to-orange-600 text-white rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform
                mb-0 mr-4 mx-0">
                <benefit.icon className="text-xl" />
            </div>
            <p className="font-medium text-gray-700">{benefit.text}</p>
        </motion.div>
    ))}
</div>
                    </motion.div>

                    {/* CTA Section */}
                    {/* <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center mt-16"
                >
                    <h3 className="text-3xl font-bold mb-4 text-gray-800">
                        Ready to Meet Your AI Assistant?
                    </h3>
                    <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                        Join 500+ restaurants already using AI to boost their sales and improve customer experience.
                    </p>


                </motion.div> */}
                </div>
            </section>
        </Element>
    );
};

export default AIChatbotSection;